/**
 * Tailwind CSS implementation of Switch component
 * Uses Seeds switch CSS classes from switch.css
 */

import * as React from "react";
import Icon from "@sproutsocial/seeds-react-icon";
import type { TypeSwitchProps } from "./SwitchTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const SwitchTailwind = React.forwardRef<
  HTMLButtonElement,
  TypeSwitchProps
>(
  (
    {
      onClick,
      loading = false,
      checked,
      disabled = false,
      className,
      ...rest
    }: TypeSwitchProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    const handleClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
      if (!disabled) {
        onClick(e, !checked);
      }
    };

    // Render through an `any`-typed element so spreading {...rest} (which is
    // typed as still carrying styled-system props from TypeSwitchProps) is not
    // rejected against the strict native ButtonHTMLAttributes. This mirrors
    // ButtonTailwind.tsx. At runtime the Hybrid only routes here when
    // !hasStyledProps(rest), so no system props are ever present.
    const Component = "button" as any;

    return (
      <Component
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={handleClick}
        className={cn("seeds-switch", { loading }, className)}
        ref={ref}
        {...rest}
      >
        {checked ? <Icon aria-hidden size="mini" name="check-solid" /> : null}
      </Component>
    );
  }
);

SwitchTailwind.displayName = "SwitchTailwind";
